/** @format */

import { Chain, Environment, LocalStorageConfig } from '@shared/data-access/interfaces';
import { CustomUrlSerializer, WindowModule, LoggerConfig } from '@shared/data-access/services';
import { DCSG_APP_NAME, GlobalErrorHandler, LogDataAccessModule } from '@logger';
import { ErrorHandler, NgModule } from '@angular/core';
import { IConfig, provideEnvironmentNgxMask } from 'ngx-mask';
import { RouterModule, Routes, UrlSerializer } from '@angular/router';
import { SimpleLoginFooterModule, SimpleLoginHeaderModule } from '@my-account/components';

import { APP_BASE_HREF } from '@angular/common';
import { AUTH_URL_FILTER } from '@shared/authentication/data-access';
/*
 * App Components
 */
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { CeopsComponent } from '@shared/ceops';
/*
 * App Interceptors
 */
import { HTTP_INTERCEPTOR_PROVIDERS } from './app.interceptors';
import { HttpClientModule } from '@angular/common/http';
import { MatDialogModule } from '@angular/material/dialog';
/*
 * App Modules
 */
import { MyAccountModule } from './myaccount/myaccount.module';
import { NikeConnectOverlayModule } from '@shared/components';
/*
 * App Environment
 */
import { chain } from '@core/chain';
import { environment } from '@environment/myaccount';

const MASK_CONFIG: Partial<IConfig> = {
	validation: false
};

/*
 * App Routes
 */
const APP_ROUTES: Routes = [
	{
		path: '**',
		loadChildren: () => import('@my-account/templates').then((m) => m.NotFoundModule)
	}
];

/*
 * App Configs
 */
const LOCAL_STORAGE_CONFIG: LocalStorageConfig = {
	prefix: ''
};

const LOGGER_CONFIG: LoggerConfig = {
	clientLoggerLevel: environment.logging.client.level,
	xhrLoggerLevel: environment.logging.xhr.level,
	xhrLoggerUrl: `${environment.api.spring.microServiceProxy.logger.path}publish`
};

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserAnimationsModule,
		BrowserModule,
		WindowModule.forRoot(LOCAL_STORAGE_CONFIG),
		LogDataAccessModule.forRoot(LOGGER_CONFIG),
		CeopsComponent,
		HttpClientModule,
		MatDialogModule,
		RouterModule.forRoot(APP_ROUTES, {
			useHash: Boolean(history.pushState) === false,
			initialNavigation: 'enabledBlocking',
			onSameUrlNavigation: 'ignore',
			enableTracing: false,
			anchorScrolling: 'enabled',
			scrollPositionRestoration: 'enabled'
		}),
		MyAccountModule,
		NikeConnectOverlayModule,
		SimpleLoginFooterModule,
		SimpleLoginHeaderModule
	],
	providers: [
		{ provide: APP_BASE_HREF, useValue: '/' },
		{
			provide: AUTH_URL_FILTER,
			useValue: [
				environment.baseUrls.addressService,
				environment.baseUrls.athleteService,
				environment.baseUrls.authService,
				environment.baseUrls.automatedToolingService,
				environment.baseUrls.orderService,
				environment.baseUrls.pointService,
				environment.baseUrls.segmentationService,
				environment.baseUrls.consumerApi,
				environment.baseUrls.identityService,
				environment.baseUrls.emailValidationService,
				environment.baseUrls.batfittingService,
				environment.baseUrls.passwordApi,
				environment.baseUrls.sendPasswordResetLink,
				environment.baseUrls.athleteManagementService,
				environment.baseUrls.creditApi,
				environment.baseUrls.mfaApi,
				environment.baseUrls.verificationApi,
				environment.baseUrls.athleteProfileApi,
				environment.api.myLocker.path,
				environment.baseUrls.orders,
				environment.baseUrls.gameTimeApi,
				environment.baseUrls.partyService
			]
		},
		{ provide: Chain, useValue: chain || {} },
		{ provide: DCSG_APP_NAME, useValue: 'MYACCOUNT' },
		{ provide: Environment, useValue: environment || {} },
		{ provide: ErrorHandler, useClass: GlobalErrorHandler },
		...HTTP_INTERCEPTOR_PROVIDERS,
		{ provide: UrlSerializer, useClass: CustomUrlSerializer },
		provideEnvironmentNgxMask(MASK_CONFIG)
	],
	bootstrap: [AppComponent]
})
export class AppModule {}
